"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CountriesPopup = exports.CountryDropdown = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const i18n_1 = require("@completeanatomy/i18n");
const ui_1 = require("@completeanatomy/ui");
const react_1 = require("react");
const styled_components_1 = __importDefault(require("styled-components"));
const DropdownWithSearchArrow_1 = __importDefault(require("../DropdownWithSearchArrow/DropdownWithSearchArrow"));
function CountryDropdown({ className, onSelect, onToggleBtn, setCountry, selectedCountry, }) {
    const { i18n, t } = (0, i18n_1.useTranslation)();
    const [isPopupOpen, setPopupOpen] = (0, react_1.useState)(false);
    const [searchValue, setSearchValue] = (0, react_1.useState)('');
    const [focusedIndex, setFocusedIndex] = (0, react_1.useState)(null);
    const optionsRef = (0, react_1.useRef)([]);
    const items = Object.entries(i18n_1.Strings.countries).map(([key]) => ({
        code: key,
        label: t(i18n_1.Strings.countries[key]),
    }));
    const [countries, setCountries] = (0, ui_1.useSortedCountries)(items, i18n.language);
    const onSelectCountry = (0, react_1.useCallback)((country) => {
        setSearchValue(country.label);
        onSelect(country);
        const filteredItems = items.filter((item) => item.label === country.label);
        setCountries(filteredItems);
        setPopupOpen(false);
    }, [items, onSelect, setCountries]);
    const onSearch = (value) => {
        setSearchValue(value);
        const filteredItems = items.filter((country) => country.label.toLowerCase().includes(value.toLowerCase()));
        setCountries(filteredItems);
        const currentCountry = items.find((country) => country.label === value);
        if (!currentCountry) {
            setCountry(null);
        }
    };
    const onTogglePopup = () => {
        if (!selectedCountry) {
            onToggleBtn(false);
        }
        setPopupOpen(!isPopupOpen);
    };
    const onKeyDown = (0, react_1.useCallback)((event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setPopupOpen(false);
            setFocusedIndex(null);
        }
        else if (event.key === 'ArrowDown') {
            event.preventDefault();
            setFocusedIndex(prevIndex => prevIndex === null ? 0 : (prevIndex + 1) % countries.length);
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setFocusedIndex(prevIndex => prevIndex === null
                ? countries.length - 1
                : (prevIndex - 1 + countries.length) % countries.length);
        }
        else if (event.key === 'Enter' && focusedIndex !== null) {
            event.preventDefault();
            onSelectCountry(countries[focusedIndex]);
            setFocusedIndex(null);
        }
    }, [countries, focusedIndex, onSelectCountry]);
    (0, react_1.useEffect)(() => {
        if (isPopupOpen) {
            window.addEventListener('keydown', onKeyDown);
        }
        else {
            window.removeEventListener('keydown', onKeyDown);
        }
        return () => {
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isPopupOpen, focusedIndex, countries, onKeyDown]);
    (0, react_1.useEffect)(() => {
        var _a;
        if (focusedIndex !== null && optionsRef.current[focusedIndex]) {
            (_a = optionsRef.current[focusedIndex]) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [focusedIndex]);
    const renderItems = () => ((0, jsx_runtime_1.jsx)(exports.CountriesPopup, { id: "country-list", role: "listbox", "aria-labelledby": "country-label", "aria-activedescendant": focusedIndex !== null ? `option-${focusedIndex}` : undefined, tabIndex: -1, "data-e2e": 'CountryDropdown_CountriesPopup', children: countries.map((country, index) => ((0, jsx_runtime_1.jsx)(Item, { id: `option-${index}`, role: "option", "aria-selected": focusedIndex === index, onClick: () => onSelectCountry(country), tabIndex: focusedIndex === index ? 0 : -1, ref: el => (optionsRef.current[index] = el), "data-e2e": 'CountryDropdown_Item', children: country.label }, index))) }));
    return ((0, jsx_runtime_1.jsx)(StyledDropdownWithSearch, { className: className, isOpen: isPopupOpen, currentValue: searchValue, placeholder: t(i18n_1.Strings.regionSelector.countryPlaceholder), popupContent: renderItems, onTogglePopup: onTogglePopup, onSearch: onSearch, "data-e2e": 'CountryDropdown_StyledDropdownWithSearch' }));
}
exports.CountryDropdown = CountryDropdown;
const StyledDropdownWithSearch = (0, styled_components_1.default)(DropdownWithSearchArrow_1.default) `
  z-index: 1;
  border-radius: ${props => (props.isOpen ? `4px 4px 0px 0px` : `4px`)};
  font-size: 16px;
  padding: 5px 0;
  width: 300px;
  background-color: ${props => props.theme.settings.header};

  ::placeholder {
    font-size: 16px;
    color: ${props => props.theme.tools.header.textbox.colors.white};
  }

  & Icon {
    color: ${props => props.theme.tools.header.textbox.colors.white};
    width: 40px;
    height: 40px;
  }

  & Input {
    font-size: 16px;
  }
`;
const Item = styled_components_1.default.div `
  position: relative;
  box-sizing: border-box;
  flex-shrink: 0;
  margin: 0 10px;
  padding: 10px 14px;
  overflow: hidden;

  color: ${props => props.theme.white};
  white-space: nowrap;

  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  color: ${props => props.theme.tools.header.textbox.colors.white};
  height: 48px;
  font-size: 16px;
  text-align: left;

  &:not(:first-of-type) {
    &::after {
      position: absolute;
      top: 0;
      left: 10px;

      width: 95%;
      height: 1px;

      border-top: solid 1px ${props => props.theme.disabledSecondary};

      content: ' ';
    }
  }

  border-radius: 4px;

  &:focus {
    outline: ${({ theme }) => theme.greenSecondary} solid 2px;
  }

  &.onMouse:focus {
    outline: none;
  }

  &:focus-visible {
    outline: ${({ theme }) => theme.greenSecondary} solid 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;
exports.CountriesPopup = styled_components_1.default.div `
  position: absolute;
  top: 100%;
  left: 0;
  /* show popup over all */
  z-index: 1;

  display: flex;
  flex-direction: column;

  width: 100%;
  max-height: 200px;

  overflow-x: hidden;
  overflow-y: scroll;

  border-radius: 0px 0px 4px 4px;
  background-color: ${props => props.theme.settings.header};
  box-shadow: ${props => props.theme.lectures.buttonShadow};

  &::-webkit-scrollbar {
    width: 6px;

    -webkit-appearance: none;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.scrollView.thumb};
    border-radius: 6px;
  }
`;
